import React, { useState } from "react";
import { useProposal } from "../../context/ProposalContext";
import { ELECTION_TYPES, GOVERNING_REGULATIONS } from "../../lib/costEngine";
import { EventUploadDropzone } from "./EventUploadDropzone";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export function validateEvent(e) {
  const errors = {};
  if (!e.eventTitle || !e.eventTitle.trim()) errors.eventTitle = "Event title is required.";
  const m = Number(e.membershipSize);
  if (!e.membershipSize || isNaN(m) || m <= 0) errors.membershipSize = "Enter a valid membership size.";
  if (!e.electionType) errors.electionType = "Select an election type.";
  if (!e.governingRegulation) errors.governingRegulation = "Select the governing regulations.";
  const t = Number(e.voterTurnout);
  if (e.voterTurnout === "" || isNaN(t) || t < 0 || t > 100)
    errors.voterTurnout = "Turnout must be between 0 and 100.";
  if (!e.location || !e.location.trim()) errors.location = "Location is required.";
  if (e.startDate && e.endDate && e.startDate > e.endDate)
    errors.endDate = "End date must be after start date.";
  return errors;
}

export default function Step1EventDetails({ onNext }) {
  const { state, dispatch } = useProposal();
  const { event } = state;
  const [errors, setErrors] = useState({});

  const update = (payload) => dispatch({ type: "UPDATE_EVENT", payload });

  const submit = () => {
    const errs = validateEvent(event);
    setErrors(errs);
    if (Object.keys(errs).length === 0) onNext();
    else toast.error("Please fix the highlighted fields.");
  };

  const errClass = (k) => (errors[k] ? "border-destructive focus-visible:ring-destructive" : "");

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-700 text-2xl text-foreground">Event Details</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Enter your voting event parameters, or upload a governing document to auto-fill.
        </p>
      </div>

      <EventUploadDropzone onFields={update} />

      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2 space-y-1.5">
          <Label htmlFor="eventTitle">Event Title</Label>
          <Input
            id="eventTitle"
            data-testid="input-eventTitle"
            placeholder="e.g. 2026 Chief & Council Election"
            value={event.eventTitle}
            onChange={(e) => update({ eventTitle: e.target.value })}
            className={errClass("eventTitle")}
          />
          {errors.eventTitle && (
            <p data-testid="error-eventTitle" className="text-xs text-destructive">{errors.eventTitle}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="membershipSize">Membership Size</Label>
          <Input
            id="membershipSize"
            data-testid="input-membershipSize"
            type="number"
            min="1"
            placeholder="e.g. 1200"
            value={event.membershipSize}
            onChange={(e) => update({ membershipSize: e.target.value })}
            className={errClass("membershipSize")}
          />
          {errors.membershipSize && (
            <p data-testid="error-membershipSize" className="text-xs text-destructive">{errors.membershipSize}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Election Type</Label>
          <Select value={event.electionType} onValueChange={(v) => update({ electionType: v })}>
            <SelectTrigger data-testid="select-electionType" className={errClass("electionType")}>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ELECTION_TYPES).map(([key, val]) => (
                <SelectItem key={key} value={key} data-testid={`option-${key}`}>
                  {val.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.electionType && (
            <p data-testid="error-electionType" className="text-xs text-destructive">{errors.electionType}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Governing Regulations</Label>
          <Select value={event.governingRegulation} onValueChange={(v) => update({ governingRegulation: v })}>
            <SelectTrigger data-testid="select-governingRegulation" className={errClass("governingRegulation")}>
              <SelectValue placeholder="Select governing regulations" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(GOVERNING_REGULATIONS).map(([key, label]) => (
                <SelectItem key={key} value={key} data-testid={`reg-option-${key}`}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.governingRegulation && (
            <p data-testid="error-governingRegulation" className="text-xs text-destructive">{errors.governingRegulation}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Electronic Voting Permitted?</Label>
          <Select value={event.electronicVoting} onValueChange={(v) => update({ electronicVoting: v })}>
            <SelectTrigger data-testid="select-electronicVoting">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes" data-testid="ev-option-yes">Yes</SelectItem>
              <SelectItem value="no" data-testid="ev-option-no">No</SelectItem>
              <SelectItem value="unsure" data-testid="ev-option-unsure">Not sure yet</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="internationalVoters">International Voters</Label>
          <Input
            id="internationalVoters"
            data-testid="input-internationalVoters"
            type="number"
            min="0"
            placeholder="Members residing outside Canada"
            value={event.internationalVoters}
            onChange={(e) => update({ internationalVoters: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">Members who vote from outside Canada (international mail-out rate applies).</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pollingLocations">Number of Polling / Voting Locations</Label>
          <Input
            id="pollingLocations"
            data-testid="input-pollingLocations"
            type="number"
            min="1"
            placeholder="Total polling locations"
            value={event.pollingLocations}
            onChange={(e) => update({ pollingLocations: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">The first location is included in the base cost; each additional location is $2,500.</p>
        </div>


        <div className="space-y-1.5">
          <Label htmlFor="startDate">Start Date</Label>
          <Input
            id="startDate"
            data-testid="input-startDate"
            type="date"
            value={event.startDate}
            onChange={(e) => update({ startDate: e.target.value })}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="endDate">End Date</Label>
          <Input
            id="endDate"
            data-testid="input-endDate"
            type="date"
            value={event.endDate}
            onChange={(e) => update({ endDate: e.target.value })}
            className={errClass("endDate")}
          />
          {errors.endDate && (
            <p data-testid="error-endDate" className="text-xs text-destructive">{errors.endDate}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="voterTurnout">Estimated Voter Turnout (%)</Label>
          <Input
            id="voterTurnout"
            data-testid="input-voterTurnout"
            type="number"
            min="0"
            max="100"
            placeholder="e.g. 65"
            value={event.voterTurnout}
            onChange={(e) => update({ voterTurnout: e.target.value })}
            className={errClass("voterTurnout")}
          />
          {Number(event.membershipSize) > 0 && (
            <p data-testid="estimated-voters-hint" className="text-xs text-primary">
              ≈ {Math.round((Number(event.membershipSize) * (Number(event.voterTurnout) || 0)) / 100).toLocaleString()} voters
              {" "}(auto-calculated at 60% of membership by default)
            </p>
          )}
          {errors.voterTurnout && (
            <p data-testid="error-voterTurnout" className="text-xs text-destructive">{errors.voterTurnout}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="location">Location</Label>
          <Input
            id="location"
            data-testid="input-location"
            placeholder="e.g. British Columbia, Canada"
            value={event.location}
            onChange={(e) => update({ location: e.target.value })}
            className={errClass("location")}
          />
          {errors.location && (
            <p data-testid="error-location" className="text-xs text-destructive">{errors.location}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button
          data-testid="next-to-estimate-btn"
          onClick={submit}
          className="gap-2 transition-transform hover:-translate-y-0.5 hover:shadow-md"
        >
          Continue to Estimate <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
